import { useApolloClient } from '@apollo/client';
import {
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  type ExplorerBulkAction,
  type ExplorerDataProvider,
  type ExplorerDataProviderConnection,
  filterToPostGraphileFilter,
  FilterTypes,
  type FilterValues,
  IconName,
  type ItemSelection,
  type ModalComponentProps,
  NavigationExplorer,
  PageHeaderActionType,
  sortToPostGraphileOrderBy,
  transformRange,
  useModal,
} from '@axinom/mosaic-ui';
import React, { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  type ExportServiceAccountsMutationFn,
  type ServiceAccountFilter,
  ServiceAccountsDocument,
  ServiceAccountsOrderBy,
  type ServiceAccountsQuery,
  type ServiceAccountsQueryVariables,
  useExportServiceAccountsMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { ServiceAccountImportModal } from '../ServiceAccountsImport/ServiceAccountImportModal';

type ServiceAccountData = NonNullable<
  ServiceAccountsQuery['filtered']
>['nodes'][number];

function stripTypename<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(stripTypename) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => key !== '__typename')
        .map(([key, val]) => [key, stripTypename(val)]),
    ) as T;
  }
  return value;
}

function downloadJson(fileName: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

async function exportServiceAccountsToFile(
  exportServiceAccounts: ExportServiceAccountsMutationFn,
  envId: string,
  filter: ServiceAccountFilter | undefined,
): Promise<void> {
  const result = await exportServiceAccounts({
    variables: { condition: { environmentId: envId }, filter },
  });
  const payload = result.data?.exportServiceAccounts;
  if (payload) {
    const date = payload.exportedAt.slice(0, 10);
    downloadJson(`service-accounts-${date}.json`, stripTypename(payload));
  }
}

export const ServiceAccounts: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const apolloClient = useApolloClient();

  const explorerRef =
    useRef<ExplorerDataProviderConnection<ServiceAccountData>>(null);

  const onImported = useCallback(() => {
    explorerRef.current?.reload();
  }, []);

  // Kept stable, as a new component on every render would remount the open modal
  const ImportModal = useCallback(
    ({ closeModal }: ModalComponentProps) => (
      <ServiceAccountImportModal
        closeModal={closeModal}
        onImported={onImported}
      />
    ),
    [onImported],
  );

  const { ModalWrapper, openModal } = useModal(ImportModal);

  const [exportServiceAccounts] = useExportServiceAccountsMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const exportBulkAction: ExplorerBulkAction<ServiceAccountData> = {
    label: 'Export',
    onClick: async (arg?: ItemSelection<ServiceAccountData>) => {
      let filter;
      switch (arg?.mode) {
        case 'SELECT_ALL':
          filter = transformServiceAccountsFilters(arg.filters);
          break;
        case 'SINGLE_ITEMS':
          filter = { id: { in: arg.items?.map((item) => item.id) } };
          break;
      }

      await exportServiceAccountsToFile(exportServiceAccounts, envId, filter);
    },
    icon: IconName.Export,
    actionType: PageHeaderActionType.Context,
    reloadData: false,
  };

  // Columns
  const explorerColumns: Column<ServiceAccountData>[] = [
    { label: 'Name', propertyName: 'name', size: '2fr' },
    { label: 'Client ID', propertyName: 'clientId' },
    { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
    {
      label: 'Last Modified At',
      propertyName: 'updatedAt',
      render: DateRenderer,
    },
  ];

  // Data provider
  const dataProvider: ExplorerDataProvider<ServiceAccountData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const result = await apolloClient.query<
        ServiceAccountsQuery,
        ServiceAccountsQueryVariables
      >({
        query: ServiceAccountsDocument,
        variables: {
          condition: {
            environmentId: envId,
          },
          filter: transformServiceAccountsFilters(filters),
          orderBy: sortToPostGraphileOrderBy(sorting, ServiceAccountsOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      return {
        data: result.data.filtered?.nodes ?? [],
        totalCount: result.data.nonFiltered?.totalCount as number,
        filteredCount: result.data.filtered?.totalCount as number,
        hasMoreData: result.data.filtered?.pageInfo.hasNextPage || false,
        pagingInformation: result.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<ServiceAccountData>();

  return (
    <div>
      <NavigationExplorer<ServiceAccountData>
        ref={explorerRef}
        title="Service Accounts"
        stationKey="ServiceAccountsExplorer"
        columns={explorerColumns}
        dataProvider={dataProvider}
        calculateNavigateUrl={(item) =>
          `/env/${envId}/serviceAccounts/${item.id}`
        }
        onCreateAction={`/env/${envId}/serviceAccounts/create`}
        bulkActions={[exportBulkAction]}
        actions={[
          {
            label: 'Import',
            icon: IconName.Import,
            onClick: () => {
              openModal();
            },
          },
        ]}
        defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
        filterOptions={[
          {
            label: 'Name',
            property: 'name',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Client ID',
            property: 'clientId',
            type: FilterTypes.FreeText,
          },
          {
            label: 'Creation Period (From)',
            property: 'createdAt',
            type: FilterTypes.Date,
            onValidate: createFromDateFilterValidator('createdAt'),
          },
          {
            label: 'Creation Period (To)',
            property: 'createdAt',
            type: FilterTypes.Date,
            onValidate: createToDateFilterValidator('createdAt'),
          },
          {
            label: 'Modification Period (From)',
            property: 'updatedAt',
            type: FilterTypes.Date,
            onValidate: createFromDateFilterValidator('updatedAt'),
          },
          {
            label: 'Modification Period (To)',
            property: 'updatedAt',
            type: FilterTypes.Date,
            onValidate: createToDateFilterValidator('updatedAt'),
          },
        ]}
      />
      <ModalWrapper />
    </div>
  );
};

const transformServiceAccountsFilters = (
  filters: FilterValues<ServiceAccountData>,
): ServiceAccountFilter | undefined => {
  return filterToPostGraphileFilter<ServiceAccountFilter>(filters, {
    name: 'includesInsensitive',
    clientId: 'includesInsensitive',
    createdAt: transformRange,
    updatedAt: transformRange,
  });
};

export const ServiceAccountsCrumb: BreadcrumbResolver = () =>
  'Service Accounts';
